import * as Dialog from '@radix-ui/react-dialog';
import { Eye, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  previewNotificationTemplateSchema,
  type NotificationTemplateDetail,
  type NotificationTemplatePreviewResponse,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input } from '../../clients/form-primitives';
import { PillButton } from '../../clients/PillButton';
import { extractErrorMessage } from '../../../lib/errors';
import { usePreviewTemplate } from '../../../hooks/use-notification-templates';

interface PreviewModalProps {
  template: NotificationTemplateDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PreviewFormState {
  sampleClientId: string;
  sampleChargeId: string;
  sampleSubscriptionId: string;
}

const initialPreview: PreviewFormState = {
  sampleClientId: '',
  sampleChargeId: '',
  sampleSubscriptionId: '',
};

export function PreviewModal({
  template,
  open,
  onOpenChange,
}: PreviewModalProps): JSX.Element | null {
  const [form, setForm] = useState<PreviewFormState>(initialPreview);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [rendered, setRendered] = useState<NotificationTemplatePreviewResponse | null>(null);
  const previewMutation = usePreviewTemplate();

  useEffect(() => {
    if (open) {
      setForm(initialPreview);
      setFieldErrors({});
      setRendered(null);
      previewMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template?.id]);

  if (!template) return null;

  const isEmail = template.channel === 'email';

  async function handlePreview(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!template) return;
    setFieldErrors({});

    const dto = {
      sample_client_id: form.sampleClientId.trim(),
      ...(form.sampleChargeId.trim() ? { sample_charge_id: form.sampleChargeId.trim() } : {}),
      ...(form.sampleSubscriptionId.trim()
        ? { sample_subscription_id: form.sampleSubscriptionId.trim() }
        : {}),
    };
    const parsed = previewNotificationTemplateSchema.safeParse(dto);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0]?.toString() ?? '_';
        errors[path] = 'UUID inválido';
      }
      setFieldErrors(errors);
      return;
    }
    try {
      const result = await previewMutation.mutateAsync({ id: template.id, dto: parsed.data });
      setRendered(result);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao gerar preview.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(720px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="flex items-center gap-2 text-[18px] font-semibold tracking-[-0.005em] text-ink">
                <Eye size={16} strokeWidth={1.8} className="text-brand" />
                Preview — {template.key}
              </Dialog.Title>
              <p className="mt-1 text-[12px] text-muted">
                Canal: {template.channel} · Versão {template.version}
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handlePreview} className="flex flex-col gap-4">
            <Field
              label="Sample client ID (UUID)"
              required
              hint="UUID de um cliente real pra renderizar variáveis"
              error={fieldErrors.sample_client_id}
            >
              {({ inputId, describedBy }) => (
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  value={form.sampleClientId}
                  onChange={(e) => setForm((prev) => ({ ...prev, sampleClientId: e.target.value }))}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  hasError={Boolean(fieldErrors.sample_client_id)}
                />
              )}
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Sample charge ID (opcional)" error={fieldErrors.sample_charge_id}>
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    value={form.sampleChargeId}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, sampleChargeId: e.target.value }))
                    }
                    placeholder="opcional"
                    hasError={Boolean(fieldErrors.sample_charge_id)}
                  />
                )}
              </Field>

              <Field
                label="Sample subscription ID (opcional)"
                error={fieldErrors.sample_subscription_id}
              >
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    value={form.sampleSubscriptionId}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, sampleSubscriptionId: e.target.value }))
                    }
                    placeholder="opcional"
                    hasError={Boolean(fieldErrors.sample_subscription_id)}
                  />
                )}
              </Field>
            </div>

            <div className="flex items-center justify-end gap-2">
              <PillButton type="submit" solid disabled={previewMutation.isPending}>
                {previewMutation.isPending ? 'Renderizando…' : 'Gerar preview'}
              </PillButton>
            </div>
          </form>

          {rendered && (
            <div className="mt-6 flex flex-col gap-4 border-t border-line pt-6">
              {isEmail && (
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-wider text-muted-3">Assunto</span>
                  <div className="rounded-[8px] border border-line bg-panel-2 px-3 py-2 text-[13px] text-ink">
                    {rendered.subject ?? <span className="text-muted">(sem assunto)</span>}
                  </div>
                </div>
              )}

              {isEmail && rendered.bodyHtml && (
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-wider text-muted-3">
                    Corpo HTML
                  </span>
                  <iframe
                    title="Email preview"
                    sandbox=""
                    srcDoc={rendered.bodyHtml}
                    className="h-[320px] w-full rounded-[8px] border border-line bg-white"
                  />
                </div>
              )}

              <div className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wider text-muted-3">
                  Corpo texto
                </span>
                <pre className="overflow-x-auto whitespace-pre-wrap rounded-[8px] border border-line bg-panel-2 p-3 font-mono text-[12px] text-ink">
                  {rendered.bodyText}
                </pre>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
